import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useActiveFamily } from "./ActiveFamilyContext";
import { useAuth } from "./AuthContext";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

export type FamilyRole = "owner" | "admin" | "member" | "guest" | null;

interface FamilyPermissions {
  role: FamilyRole;
  isOwner: boolean;
  isAdmin: boolean;
  canManageMembers: boolean;
  canManageActivities: boolean;
  canManageGroups: boolean;
  canCreateTasks: boolean;
  canViewReports: boolean;
  canManageSettings: boolean;
}

interface FamilyPermissionsContextType {
  permissions: FamilyPermissions;
  loading: boolean;
  refreshPermissions: () => Promise<void>;
}

const defaultPermissions: FamilyPermissions = {
  role: null,
  isOwner: false,
  isAdmin: false,
  canManageMembers: false,
  canManageActivities: false,
  canManageGroups: false,
  canCreateTasks: false,
  canViewReports: false,
  canManageSettings: false,
};

const FamilyPermissionsContext = createContext<FamilyPermissionsContextType | undefined>(undefined);

export function FamilyPermissionsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { activeFamily } = useActiveFamily();
  const [permissions, setPermissions] = useState<FamilyPermissions>(defaultPermissions);
  const [loading, setLoading] = useState(true);

  const calculatePermissions = async (): Promise<FamilyPermissions> => {
    if (!user?.uid || !activeFamily?.id) {
      return defaultPermissions;
    }

    try {
      // Obtener datos del usuario
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (!userDoc.exists()) {
        return defaultPermissions;
      }

      const userData = userDoc.data();
      const userRole = userData.role as string;

      // Verificar si es owner del grupo activo
      const isOwner = activeFamily.ownerId === user.uid;

      // Verificar si es admin del grupo activo
      const isAdmin = isOwner || (Array.isArray(activeFamily.admins) && activeFamily.admins.includes(user.uid));

      // Verificar si es superadmin (tiene permisos globales)
      const isSuperAdmin = userRole === "superadmin";

      // Determinar el rol en este grupo específico
      let role: FamilyRole = "member";
      if (isSuperAdmin) {
        role = "admin"; // Superadmin actúa como admin en todos los grupos
      } else if (isOwner) {
        role = "admin"; // Owner tiene permisos de admin
      } else if (isAdmin) {
        role = "admin";
      } else if (userRole === "guest") {
        role = "guest";
      }

      // Calcular permisos basados en el rol
      // Owner y Admin siempre pueden crear tareas
      // También si el usuario tiene explícitamente canCreateTasks = true
      const canCreateTasks = isSuperAdmin || isAdmin || userData.canCreateTasks === true;

      return {
        role,
        isOwner: isOwner || isSuperAdmin,
        isAdmin: isAdmin || isSuperAdmin,
        canManageMembers: isAdmin || isSuperAdmin,
        canManageActivities: canCreateTasks,
        canManageGroups: isOwner || isSuperAdmin,
        canCreateTasks,
        canViewReports: isAdmin || isSuperAdmin,
        canManageSettings: isOwner || isSuperAdmin,
      };
    } catch (error) {
      return defaultPermissions;
    }
  };

  const refreshPermissions = async () => {
    setLoading(true);
    const newPermissions = await calculatePermissions();
    setPermissions(newPermissions);
    setLoading(false);
  };

  useEffect(() => {
    const loadPermissions = async () => {
      // Si hay usuario pero aún no hay familia activa, resolver con permisos por defecto.
      // Mantener loading=true acá puede dejar al UI colgado en "Verificando permisos..."
      // (por ejemplo: al saltear onboarding o tras eliminar la cuenta).
      if (user?.uid && !activeFamily?.id) {
        setPermissions(defaultPermissions);
        setLoading(false);
        return;
      }

      setLoading(true);
      const perms = await calculatePermissions();
      setPermissions(perms);
      setLoading(false);
    };

    loadPermissions();
  }, [user?.uid, activeFamily?.id]);

  return (
    <FamilyPermissionsContext.Provider value={{ permissions, loading, refreshPermissions }}>
      {children}
    </FamilyPermissionsContext.Provider>
  );
}

export function useFamilyPermissions(): FamilyPermissionsContextType {
  const context = useContext(FamilyPermissionsContext);
  if (context === undefined) {
    throw new Error("useFamilyPermissions must be used within a FamilyPermissionsProvider");
  }
  return context;
}
